
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useRememberedScroll } from '../hooks/useRemembered';
import { supabase } from '../services/supabaseClient';
import { UserProfile, AlertConfig, ReportMetadata, AppView } from '../types';
import { Shield, LayoutDashboard, FolderCog, ListChecks, ArrowRightLeft, Settings as SettingsIcon, UserPlus, Bell, Send, ClipboardList, BookOpen, Bug, Activity, ShieldQuestion, ShieldAlert, Camera, GraduationCap, KeyRound, FileText, Receipt, ListTree, Printer, Wrench, Cloud } from 'lucide-react';
import { loadProjectsFromCloud, fetchOpenBugCount, patchProfileSettings } from '../services/cloudService';
import type { ListProject } from '../services/listProject';
import { saveProjectSupplement } from '../services/projectSupplement';
import { contractedQuantity } from '../services/nepPosition';
import { subscribeWithAuthRecovery } from '../services/realtimeResilience';
import { sameCompany } from '../services/companyScope';
import { AdminDashboard } from './AdminDashboard';
import { KontextHilfe } from './lernzentrum/KontextHilfe';
import type { TutorialKapitelId } from '../tutorial/types';

// New Sub-Components
import { AdminProjectList } from './admin/AdminProjectList';
import { AdminUserManagement } from './admin/AdminUserManagement';
import { AdminCatalogEditor } from './admin/AdminCatalogEditor';
import { AdminGlobalSettings, type EinstellungsAbschnitt } from './admin/AdminGlobalSettings';
import { einstellungenGeschrieben } from './admin/einstellungenSchreiben';
import { AdminApiLogsView } from './admin/AdminApiLogsView';
import { ADMIN_BEREICHE, istAdminBereichView, tabImBereich, type AdminTab } from './admin/adminBereiche';
import { AdminAlertsView } from './admin/AdminAlertsView';
import { AdminTransferList } from './admin/AdminTransferList';
import { SentReportsView } from './admin/SentReportsView';
import { ProjectSupplementModal } from './ProjectSupplementModal';
import { AlertSettingsModal } from './AlertSettingsModal';

import { AdminSubstitutionView } from './admin/AdminSubstitutionView';
import { AdminOpenPositionsView } from './admin/AdminOpenPositionsView';
import { AdminIntegrityView } from './admin/AdminIntegrityView';
import { AdminReferenceSheetView } from './admin/AdminReferenceSheetView';
import { AdminProjectImagesView } from './admin/AdminProjectImagesView';
import { AdminBugReportsView } from './admin/AdminBugReportsView';
import { AdminUserActivityView } from './admin/AdminUserActivityView';
import { AdminDeletionRequests } from './admin/AdminDeletionRequests';
import { AdminLernzentrumView } from './admin/AdminLernzentrumView';
import { RollenRechteView } from './admin/RollenRechteView';
import { pruefeSchemaStand, SchemaStand } from '../services/schemaVersion';
import { abonniereRueckfall, rueckfallStand, type RueckfallStand } from '../services/spaltenRueckfall';
import { getServiceLog } from '../services/serviceJournal';
import { inspectionQuota, latestInspectionLogDate } from '../services/inspectionFigures';

interface Props {
  /**
   * Gewuenschter Startreiter. Absichtlich auch `string`: App.tsx reicht den
   * gemerkten Wert durch, und der kann von vor dem Menue-Umbau stammen
   * ('invites', 'api' …) oder zu einem anderen Bereich gehoeren.
   * `tabImBereich` faengt beides ab — gezeigt wird dann der Standardreiter.
   */
  initialTab?: AdminTab | string;
  onOpenProject?: (project: ListProject) => void;
  // Deep-Link: öffnet im Buchhaltungs-Tab direkt die Abrechnung dieses Projekts
  onCreateProject?: () => void;
  onSaveStatusChange?: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
  onRefresh?: () => void;
  onTabChange?: (tab: string) => void; // Notify parent of tab changes
  projectsProp?: ListProject[]; // Passed from App.tsx (Realtime)
  /**
   * Kontext-"?" in der Reiterleiste bzw. — bei nur einem Reiter — in der
   * Kopfzeile neben dem Titel (Lernzentrum). Ohne den Rueckruf gibt es
   * kein Fragezeichen — die Ansicht ist auch ohne Lernzentrum vollstaendig,
   * und ein Knopf, der nirgendwohin fuehrt, ist schlimmer als keiner.
   */
  onOeffneLernzentrum?: (kapitelId?: TutorialKapitelId) => void;
  /**
   * Die Ansicht, unter der diese AdminView gerade laeuft.
   *
   * Mehrere Menuepunkte rendern dieselbe Komponente; welche Reiter und
   * welcher Titel dazugehoeren, steht in `ADMIN_BEREICHE` (adminBereiche.ts).
   * Auch das Kontext-"?" braucht die ECHTE Ansicht — sonst bietet es in der
   * Benutzerverwaltung die Kapitel der Verwaltung an oder, haeufiger, gar
   * keine. Vorgabe `admin`, damit Aufrufer ohne Navigation nichts mitgeben
   * muessen.
   */
  view?: AppView;
}

/**
 * Aussehen jedes Reiters. Welche davon eine Ansicht zeigt und in welcher
 * Reihenfolge, entscheidet `ADMIN_BEREICHE` — hier steht nur, WIE ein Reiter
 * aussieht. `Record` statt Liste: kommt in adminBereiche.ts ein Reiter dazu,
 * meldet der Typpruefer die fehlende Zeile, statt dass ein leerer Knopf
 * erscheint.
 *
 * Buchhaltung fehlt absichtlich: sie ist in die Rechnungen gewandert. Sie
 * zeigt denselben Vorgang von der anderen Seite, und ein eigener Reiter neben
 * dem Modul, das die Buchungen erzeugt, trennte, was zusammengehoert.
 */
const REITER: Record<AdminTab, { label: string; Icon: React.ElementType; aktiv: string; badgeFarbe?: string }> = {
    dashboard: { label: 'KPIs', Icon: LayoutDashboard, aktiv: 'border-indigo-200 text-indigo-600' },
    projects: { label: 'Projekte', Icon: FolderCog, aktiv: 'border-indigo-200 text-indigo-600' },
    alerts: { label: 'Meldungen', Icon: Bell, aktiv: 'border-red-200 text-red-600', badgeFarbe: 'bg-red-100 text-red-700 border-red-200' },
    'sent-reports': { label: 'Sendefach', Icon: Send, aktiv: 'border-indigo-200 text-indigo-600' },
    transfers: { label: 'Transfers', Icon: ArrowRightLeft, aktiv: 'border-indigo-200 text-indigo-600', badgeFarbe: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    'deletion-requests': { label: 'Löschanträge', Icon: ShieldQuestion, aktiv: 'border-amber-200 text-amber-600', badgeFarbe: 'bg-amber-100 text-amber-700 border-amber-200' },
    users: { label: 'Benutzer', Icon: UserPlus, aktiv: 'border-indigo-200 text-indigo-600' },
    // Rollen & Rechte: Rechte-Matrix je Firma, eigene Rollen, Protokoll
    // (docs/rollensystem-plan.md §6.1). Die Rollen je Person stehen in der
    // Benutzerverwaltung.
    rollen: { label: 'Rollen & Rechte', Icon: KeyRound, aktiv: 'border-indigo-200 text-indigo-600' },
    'user-activity': { label: 'Nutzeraktivität', Icon: Activity, aktiv: 'border-indigo-200 text-indigo-600' },
    settings: { label: 'Allgemein', Icon: SettingsIcon, aktiv: 'border-indigo-200 text-indigo-600' },
    'settings-angebote': { label: 'Angebote', Icon: FileText, aktiv: 'border-indigo-200 text-indigo-600' },
    'settings-rechnungen': { label: 'Rechnungen', Icon: Receipt, aktiv: 'border-indigo-200 text-indigo-600' },
    catalog: { label: 'Leistungskatalog', Icon: ListChecks, aktiv: 'border-indigo-200 text-indigo-600' },
    maengelkatalog: { label: 'Mängelkatalog', Icon: ListTree, aktiv: 'border-indigo-200 text-indigo-600' },
    'plotter-vorlagen': { label: 'SiGe-Plan-Vorlagen', Icon: Printer, aktiv: 'border-indigo-200 text-indigo-600' },
    'reference-sheet': { label: 'Referenzblätter', Icon: BookOpen, aktiv: 'border-indigo-200 text-indigo-600' },
    'project-images': { label: 'Projektbilder', Icon: Camera, aktiv: 'border-indigo-200 text-indigo-600' },
    'open-positions': { label: 'Offene Positionen', Icon: ClipboardList, aktiv: 'border-indigo-200 text-indigo-600' },
    substitutions: { label: 'Vertretungen', Icon: ArrowRightLeft, aktiv: 'border-indigo-200 text-indigo-600' },
    integrity: { label: 'Integrität', Icon: ShieldAlert, aktiv: 'border-rose-200 text-rose-600' },
    bugs: { label: 'Bugs', Icon: Bug, aktiv: 'border-red-200 text-red-600', badgeFarbe: 'bg-red-100 text-red-700 border-red-200' },
    wartung: { label: 'Wartung', Icon: Wrench, aktiv: 'border-indigo-200 text-indigo-600' },
    // Lernzentrum: Fragen der Nutzer (Lueckenliste) und Freigabe der Kapitel.
    // Paket E, Plan §1.6/§1.8.
    lernzentrum: { label: 'Lernzentrum', Icon: GraduationCap, aktiv: 'border-blue-200 text-blue-600' },
    'api-logs': { label: 'API-Logs', Icon: Cloud, aktiv: 'border-indigo-200 text-indigo-600' },
};

/**
 * Reiter, die Teile von AdminGlobalSettings zeigen. Frueher war das EIN
 * Reiter „Einstellungen" mit allen Abschnitten untereinander; jetzt haengt
 * jeder Abschnitt dort, wo man ihn sucht (Firmeneinstellungen, Kataloge &
 * Vorlagen, System).
 */
const EINSTELLUNGS_ABSCHNITT: Partial<Record<AdminTab, EinstellungsAbschnitt>> = {
    settings: 'allgemein',
    'settings-angebote': 'angebote',
    'settings-rechnungen': 'rechnungen',
    maengelkatalog: 'maengelkatalog',
    'plotter-vorlagen': 'plotter',
    wartung: 'wartung',
};

// UI Helper for the Badges
const TabBadge = ({ count, colorClass }: { count: number, colorClass: string }) => {
    if (count <= 0) return null;
    return (
        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${colorClass} animate-pulse shadow-sm`}>
            {count}
        </span>
    );
};

export const AdminView: React.FC<Props> = ({ initialTab, onOpenProject, onCreateProject, onSaveStatusChange, onRefresh, onTabChange, projectsProp, onOeffneLernzentrum, view = 'admin' }) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [projects, setProjects] = useState<ListProject[]>(projectsProp || []);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Unbekannte Ansichten (Aufrufer ohne Navigation) laufen als `admin`.
  const bereich = istAdminBereichView(view) ? view : 'admin';
  const [activeTab, setActiveTab] = useState<string | undefined>(initialTab);
  // Der Reiter, der WIRKLICH gezeigt wird: der gewuenschte, wenn er zu diesem
  // Bereich gehoert, sonst dessen Standardreiter. Abgeleitet statt im Zustand
  // korrigiert — sonst blitzte nach einem Menuewechsel fuer einen Aufbau der
  // fremde Reiter auf, und ein gemerkter Altwert ('invites') zeigte gar nichts.
  const tab = tabImBereich(bereich, activeTab);
  const reiter = ADMIN_BEREICHE[bereich].tabs;
  const einstellungsAbschnitt = EINSTELLUNGS_ABSCHNITT[tab];
  // Zuletzt gezeigter Einstellungs-Abschnitt. Einmal eingehaengt, bleibt die
  // Einstellungs-Instanz haengen (versteckt), solange diese AdminView lebt —
  // Begruendung am Rendern unten. Angepasst im Rendern, nicht im Effekt.
  const [einstellungenZuletzt, setEinstellungenZuletzt] = useState<EinstellungsAbschnitt | null>(null);
  if (einstellungsAbschnitt && einstellungsAbschnitt !== einstellungenZuletzt) setEinstellungenZuletzt(einstellungsAbschnitt);
  // Bildlaufposition je Reiter: der Rueckweg aus einem Projekt landet dort, wo man war.
  const scrollRef = useRememberedScroll(`admin.${tab}.scroll`);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [openBugCount, setOpenBugCount] = useState(0);
  // Offene Loeschantraege. Der Zaehler kommt aus der Ansicht selbst, damit es
  // keine zweite Abfrage derselben Zeilen gibt.
  const [openDeletionCount, setOpenDeletionCount] = useState(0);

  // Global Alert Config (Loaded once)
  const [alertConfig, setAlertConfig] = useState<AlertConfig | undefined>(undefined);
  const [isAlertSettingsOpen, setIsAlertSettingsOpen] = useState(false);

  // Helper State for cross-component modals (Alerts -> Supplement)
  const [supplementModalProject, setSupplementModalProject] = useState<ListProject | null>(null);

  // Realtime Ref
  const realtimeChannelRef = useRef<any>(null);

  // Sync initialTab prop to internal state (for back navigation)
  useEffect(() => {
    if (initialTab) {
        setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
      if (projectsProp) setProjects(projectsProp);
  }, [projectsProp]);

  // Realtime Subscriptions for Admin-Specific Data
  useEffect(() => {
    // Selbstheilend gegen abgelaufene JWTs nach Standby (InvalidJWTToken "exp").
    const handle = subscribeWithAuthRecovery(supabase, () => supabase.channel('admin-changes')
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'profiles' },
            () => fetchData(true)
        )
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'user_invites' },
            () => fetchData(true)
        )
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'bug_reports' },
            () => refreshBugCount()
        ));

    realtimeChannelRef.current = handle;
    return () => handle.close();
  }, []);

  const refreshBugCount = async () => {
      setOpenBugCount(await fetchOpenBugCount());
  };

  const fetchData = async (background = false) => {
    if(!background) setLoading(true);
    try {
      refreshBugCount(); // swallows its own errors, safe fire-and-forget
      // Erst laufende Einstellungs-Schreibvorgaenge abwarten (Speichern beim
      // Aushaengen): sonst laese ein Wiederkommen direkt danach das alte Profil.
      await einstellungenGeschrieben();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
          const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
          if (data) {
              setCurrentUser(data as UserProfile);
              setAlertConfig((data as any).settings?.alertConfig);
          }
      }

      const { data: userData } = await supabase.from('profiles').select('*').order('last_name', { ascending: true });
      if (userData) setUsers(userData as UserProfile[]);

      const { data: inviteData } = await supabase.from('user_invites').select('*').order('created_at', { ascending: false });
      if (inviteData) setInvites(inviteData);

      if (!projectsProp) {
        const allProjects = await loadProjectsFromCloud();
        setProjects(allProjects || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if(!background) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tab]);

  // NEW: Global Refresh Handler (Triggers both local and parent refresh)
  const handleGlobalRefresh = async () => {
      await fetchData(true); // Local data (Users etc)
      if (onRefresh) onRefresh(); // Parent data (Projects via props)
  };

  const handleTabChange = (next: AdminTab) => {
      setActiveTab(next);
      if (onTabChange) onTabChange(next);
  };

  // Nach jedem Speichern der Einstellungen das eigene Profil frisch holen:
  // haengt der Einstellungs-Abschnitt spaeter neu ein (Umweg ueber einen
  // anderen Reiter), startet er sonst mit dem Stand von vor dem Speichern und
  // schriebe einen ganzen Block (invoiceConfig) mit Altwerten zurueck. Der
  // Realtime-Kanal auf profiles tut dasselbe, ist aber nicht verlaesslich.
  const einstellungenStatus = (status: 'idle' | 'saving' | 'saved' | 'error') => {
      onSaveStatusChange?.(status);
      if (status === 'saved') void fetchData(true);
  };

  const handleSaveAlertConfig = async (newConfig: AlertConfig) => {
    if (!currentUser) return;
    try {
        await patchProfileSettings(currentUser.id, { alertConfig: newConfig });
        setAlertConfig(newConfig);
    } catch (e) { console.error(e); }
  };

  // --- Filtering Logic for Dashboard (Keep minimal here) ---
  const filteredProjects = useMemo(() => {
      if (!currentUser) return [];
      let list = projects;
      if (currentUser.role !== 'super_admin') {
          const companyUserIds = users.filter(u => sameCompany(u, currentUser)).map(u => u.id);
          list = projects.filter(p => companyUserIds.includes(p.user_id));
      }
      return list;
  }, [projects, currentUser, users]);
  
  const filteredUsers = useMemo(() => {
      if (!currentUser) return [];
      if (currentUser.role === 'super_admin') return users;
      return users.filter(u => sameCompany(u, currentUser));
  }, [users, currentUser]);

  // --- KPI COUNTERS FOR BADGES ---
  
  // 1. Transfer Count
  const transferCount = useMemo(() => projects.filter(p => p.transfer_target_id).length, [projects]);

  // 2. Alert Count (Match AdminAlertsView logic)
  const alertCount = useMemo(() => {
      const config = alertConfig || { enableOverrun: true, enableQuota: true, quotaThreshold: 2, enableStartDocs: true, enableLatency: true, latencyDays: 20 };
      const now = new Date();
      now.setHours(0,0,0,0);
      
      let totalAlerts = 0;
      
      filteredProjects.forEach(p => {
          const meta = p.data?.metadata as ReportMetadata | undefined;
          if (!meta) return;

          const services = meta.globalData?.services || [];
          const pStatus = p.status || meta.globalData?.status;

          // 1. Archiv schweigt; Beendet meldet die ausstehende Schlussrechnung.
          if (pStatus === 'finished') return;
          if (pStatus === 'completed') {
              totalAlerts++;
              return;
          }

          // 2. Overrun
          if (config.enableOverrun) {
              // Wortgleich zu AdminAlertsView: NEP ueberschreitet nie und ist nie Turnusposition.
              const hasOverrun = services.some(s => (Number(s.quantityUsed) || 0) > contractedQuantity(s));
              if (hasOverrun) totalAlerts++;
          }

          // 3. Quota
          if (config.enableQuota) {
              // Ohne Pauschale, Nachweise eingerechnet — dieselbe Funktion wie in AdminAlertsView.
              const quota = inspectionQuota(services, getServiceLog({ metadata: meta }));
              if (quota) {
                  const { contracted, remaining: rem } = quota;
                  if (contracted > 0 && rem >= 0 && rem <= (config.quotaThreshold || 2)) totalAlerts++;
              }
          }

          // 4. Docs
          if (config.enableStartDocs) {
              const startDateStr = meta.globalData?.startDate;
              if (startDateStr) {
                  const startDate = new Date(startDateStr);
                  startDate.setHours(0,0,0,0);
                  if (now >= startDate) {
                      const vorank = services.find(s => s.lvPosition === '2.1.10' || s.assignment === 'vorank' || s.title.toLowerCase().includes('vorankündigung'));
                      const unterlage = services.find(s => s.lvPosition === '1.4.10' || s.assignment === 'unterlage' || s.title.toLowerCase().includes('unterlage'));
                      const missingVorank = vorank && ((Number(vorank.quantityUsed) || 0) === 0 && vorank.status !== 'done' && vorank.status !== 'billed');
                      const missingUnterlage = unterlage && ((Number(unterlage.quantityUsed) || 0) === 0 && unterlage.status !== 'done' && unterlage.status !== 'billed');
                      if (missingVorank || missingUnterlage) totalAlerts++;
                  }
              }
          }

          // 5. Latency
          if (config.enableLatency) {
              // Begehungsbuchung ODER Nachweis auf der Pauschale — wie AdminAlertsView.
              const latestManualDate = latestInspectionLogDate(services, getServiceLog({ metadata: meta }));

              const lastReportDateStr = p.last_inspection_date || meta.date;
              let lastDate = lastReportDateStr ? new Date(lastReportDateStr) : null;
              if (lastDate && isNaN(lastDate.getTime())) lastDate = null;
              if (latestManualDate && (!lastDate || latestManualDate > lastDate)) lastDate = latestManualDate;

              if (lastDate) {
                  lastDate.setHours(0,0,0,0);
                  const diffDays = Math.ceil((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
                  if (diffDays > (config.latencyDays || 20)) totalAlerts++;
              }
          }
      });
      return totalAlerts;
  }, [filteredProjects, alertConfig]);

  // Zaehler an den Reitern. Steht hier und nicht in REITER, weil die Werte
  // aus dem Zustand dieser Ansicht kommen — und erst NACH den Zaehlern, die
  // es liest (const vor der Deklaration waere ein TDZ-Fehler).
  const badgeZahl: Partial<Record<AdminTab, number>> = {
      alerts: alertCount,
      transfers: transferCount,
      bugs: openBugCount,
      'deletion-requests': openDeletionCount,
  };


  // Schema-Wache: Banner, KEINE Sperre. Ein Rueckstand ist nur fuer den
  // Admin-Bereich interessant — daran aendern kann ihn ohnehin nur jemand mit
  // Datenbankzugang. Der Lesepfad ist fail-open bei fehlender Tabelle, jeder
  // andere Fehler landet hier in der Konsole statt die Verwaltung zu kippen.
  const [schemaStand, setSchemaStand] = useState<SchemaStand | null>(null);
  useEffect(() => {
      pruefeSchemaStand().then(setSchemaStand).catch(e => console.warn('Schema-Stand nicht lesbar:', e));
  }, []);

  // Der Spalten-Rueckfall haengt am SELBEN Weg wie die Schema-Wache: derselbe
  // Ort, dieselbe Form, ein Warnbalken statt einer Sperre — die Anwendung soll
  // ja gerade weiterlaufen. Zwei Unterschiede gibt es: der Rueckfall entsteht
  // erst WAEHREND einer Abfrage (deshalb das Abonnement statt eines einmaligen
  // Lesens beim Aufbau), und er ist der ernstere Befund — die Wache sagt „das
  // SQL fehlt", der Rueckfall sagt „es fehlt und hat schon zugeschlagen".
  // Genau deshalb darf er nicht still bleiben: einen Ausfall bemerkt jeder
  // sofort, einen stillen Notbetrieb niemand.
  const [rueckfall, setRueckfall] = useState<RueckfallStand | null>(() => rueckfallStand());
  useEffect(() => abonniereRueckfall(setRueckfall), []);

  if (!loading && currentUser && currentUser.role !== 'admin' && currentUser.role !== 'super_admin') { return null; /* Access Denied Handled */ }

  return (
    <div ref={scrollRef} data-glass-view="verwaltung" className="h-full overflow-y-auto bg-slate-50 relative">
        {isAlertSettingsOpen && <AlertSettingsModal isOpen={isAlertSettingsOpen} onClose={() => setIsAlertSettingsOpen(false)} currentSettings={alertConfig} onSave={handleSaveAlertConfig} />}
        
        {/* Helper Modal for Cross-Component Interaction (Alerts -> Project Supplement) */}
        {supplementModalProject && (
            <ProjectSupplementModal 
                project={supplementModalProject} 
                catalog={[]} 
                currentUser={currentUser} 
                onSave={async (updated, changeLog, syncCounters) => {
                     // Derselbe Speicherpfad wie in der Projektliste. Vorher
                     // wurde der Dialog-Stand hier nur verworfen: der Nachtrag
                     // schloss ohne Fehler und ohne Wirkung.
                     onSaveStatusChange?.('saving');
                     try {
                        await saveProjectSupplement(
                            supplementModalProject.id,
                            updated,
                            changeLog,
                            currentUser,
                            syncCounters
                        );
                        handleGlobalRefresh();
                        onSaveStatusChange?.('saved');
                     } catch (e) {
                        console.error(e);
                        alert("Fehler beim Speichern des Nachtrags");
                        onSaveStatusChange?.('error');
                     }
                }}
                onClose={() => setSupplementModalProject(null)} 
            />
        )}

        <div className="max-w-7xl mx-auto p-6 animate-fade-in pb-20">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    {/* Kontext-"?" bei nur einem Reiter neben dem Titel: eine
                        Reiterleiste mit einem einzigen Knopf waere nur Laerm,
                        und das Fragezeichen braucht trotzdem einen Platz.
                        Ausserhalb der h2, weil KontextHilfe selbst eine h2
                        mitbringt. */}
                    <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Shield className="w-6 h-6 text-indigo-600" />
                            {ADMIN_BEREICHE[bereich].titel}
                        </h2>
                        {reiter.length === 1 && onOeffneLernzentrum && (
                            <KontextHilfe view={view} onOeffneLernzentrum={onOeffneLernzentrum} variant="inline" />
                        )}
                    </div>
                    <p className="text-slate-500 text-sm mt-1">
                        {currentUser?.role === 'super_admin' ? 'Verwaltung aller Benutzer.' : `Verwaltung für ${currentUser?.company_name || 'Ihre Organisation'}`}
                    </p>
                </div>
            </div>

            {schemaStand?.hinterher && (
                <div className="mb-6 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-100 px-4 py-2 text-xs font-bold text-amber-900">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span>{schemaStand.text}</span>
                </div>
            )}

            {rueckfall && (
                <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-300 bg-red-100 px-4 py-2 text-xs font-bold text-red-900">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span>{rueckfall.text}</span>
                </div>
            )}

            {/* Reiterleiste: nur die Reiter DIESES Bereichs, in dessen
                Reihenfolge (adminBereiche.ts). Bei nur einem Reiter entfaellt
                sie — das Kontext-"?" steht dann neben dem Titel. */}
            {reiter.length > 1 && (
                <div data-glass="reiter" className="flex flex-wrap gap-2 mb-6">
                    {reiter.map((key) => {
                        const { label, Icon, aktiv, badgeFarbe } = REITER[key];
                        return (
                            <button
                                key={key}
                                onClick={() => handleTabChange(key)}
                                className={`px-4 py-2 text-sm font-bold rounded-xl border transition-all flex items-center gap-2 cursor-pointer ${
                                    tab === key
                                    ? `bg-white ${aktiv} shadow-sm`
                                    : 'bg-slate-50 border-transparent text-slate-500 hover:bg-white hover:border-slate-200'
                                }`}
                            >
                                <Icon className="w-4 h-4" /> {label}
                                {badgeFarbe && <TabBadge count={badgeZahl[key] ?? 0} colorClass={badgeFarbe} />}
                            </button>
                        );
                    })}

                    {/* Kontext-"?" — rechts aussen in der Reiterleiste, abgesetzt
                        durch ml-auto. In dieser Ansicht blendet App.tsx dafuer das
                        schwebende Fragezeichen aus. */}
                    {onOeffneLernzentrum && (
                        <div className="ml-auto flex items-center">
                            <KontextHilfe view={view} onOeffneLernzentrum={onOeffneLernzentrum} variant="inline" />
                        </div>
                    )}
                </div>
            )}

            {/* TAB CONTENT — `tab`, nicht `activeTab`: gerendert wird nur, was
                zu diesem Bereich gehoert. */}
            {tab === 'dashboard' && <AdminDashboard users={filteredUsers} projects={filteredProjects} currentUser={currentUser} />}
            
            {tab === 'projects' && (
                <AdminProjectList 
                    projects={filteredProjects} 
                    users={users} 
                    currentUser={currentUser}
                    onOpenProject={onOpenProject || (() => {})}
                    onCreateProject={onCreateProject || (() => {})}
                    onRefresh={handleGlobalRefresh}
                    onSaveStatusChange={onSaveStatusChange || (() => {})}
                    customCatalog={[]} 
                />
            )}

            {tab === 'sent-reports' && <SentReportsView />}

            {tab === 'substitutions' && (
                <AdminSubstitutionView
                    projects={filteredProjects}
                    users={users}
                    onOpenProject={onOpenProject}
                />
            )}

            {tab === 'users' && (
                <AdminUserManagement 
                    users={users} 
                    invites={invites} 
                    currentUser={currentUser} 
                    onRefresh={handleGlobalRefresh} 
                />
            )}

            {/* Alle geladenen Profile: Namen im Protokoll auch für den
                Super-Admin einer anderen Firma. Welche Firma ein Admin sieht,
                entscheidet die Ansicht selbst (nur die eigene). */}
            {tab === 'rollen' && <RollenRechteView currentUser={currentUser} users={users} />}

            {tab === 'open-positions' && (
                <AdminOpenPositionsView 
                    projects={filteredProjects} 
                    users={users} 
                    onOpenProject={onOpenProject || (() => {})} 
                />
            )}

            {/* Der Integritaetswaechter laedt seine Befunde selbst (Tabelle
                integrity_findings). `projects` bekommt er nur, um von einem
                Befund ins Projekt springen zu koennen. */}
            {tab === 'integrity' && (
                <AdminIntegrityView
                    projects={filteredProjects}
                    onOpenProject={onOpenProject || (() => {})}
                />
            )}

            {tab === 'reference-sheet' && (
                <AdminReferenceSheetView
                    projects={filteredProjects}
                    users={users}
                    currentUser={currentUser}
                />
            )}

            {/* Projektbilder: Referenzbild + Beschreibung je Projekt mit Download
                (Betreiber 12.09.2026, Social Media). Reine Anzeige aus dem
                Projekt-JSON, gepflegt wird im Projekt-Hub. */}
            {tab === 'project-images' && (
                <AdminProjectImagesView
                    projects={filteredProjects}
                    onOpenProject={onOpenProject}
                />
            )}

            {tab === 'catalog' && (
                <AdminCatalogEditor 
                    onSaveStatusChange={onSaveStatusChange || (() => {})} 
                />
            )}

            {/* Ein Abschnitt der Einstellungen je Reiter — aber EINE Instanz
                fuer alle diese Reiter (kein `key`), und sie bleibt eingehaengt,
                auch wenn ein anderer Reiter vorn ist (dann versteckt): ihr
                lokaler Stand ist die Wahrheit, solange diese AdminView lebt,
                wie frueher auf der einen Seite. Jede neue Instanz startete mit
                dem Profil vom letzten Laden, und die Grussformel aus
                „Allgemein" ginge mit der naechsten Aenderung unter „Rechnungen"
                verloren (beide stehen in invoiceConfig, geschrieben wird der
                ganze Block). Erst mit geladenem Profil einhaengen: der Stand
                wird beim Aufbau aus `currentUser` gebildet und zieht nicht
                nach — mit null startete das Formular leer, und die erste
                Aenderung schriebe einen leeren Block zurueck. Frueher fiel das
                nicht auf, weil kein Menuepunkt direkt auf den Einstellungen
                landete. */}
            {einstellungenZuletzt && currentUser && (
                <div className={einstellungsAbschnitt ? undefined : 'hidden'}>
                    <AdminGlobalSettings
                        abschnitt={einstellungenZuletzt}
                        currentUser={currentUser}
                        onSaveStatusChange={einstellungenStatus}
                    />
                </div>
            )}
            {einstellungsAbschnitt && !currentUser && (
                <div className="py-16 text-center text-sm text-slate-400">Einstellungen werden geladen …</div>
            )}

            {/* API-Logs: frueher ein Reiter im KPI-Cockpit, jetzt unter System —
                es sind Betriebsdaten, keine Kennzahlen. */}
            {tab === 'api-logs' && <AdminApiLogsView />}

            {tab === 'alerts' && (
                <AdminAlertsView 
                    projects={filteredProjects} 
                    users={users}
                    currentUser={currentUser}
                    onOpenProject={onOpenProject || (() => {})}
                    onOpenAlertSettings={() => setIsAlertSettingsOpen(true)}
                    onOpenSupplement={(p) => setSupplementModalProject(p)} 
                    alertConfig={alertConfig}
                />
            )}
            
            {tab === 'transfers' && (
                <AdminTransferList
                    projects={projects}
                    users={users}
                    currentUser={currentUser}
                    onRefresh={handleGlobalRefresh}
                />
            )}

            {tab === 'bugs' && <AdminBugReportsView onCountChanged={refreshBugCount} />}

            {tab === 'user-activity' && <AdminUserActivityView />}

            {tab === 'deletion-requests' && (
                <AdminDeletionRequests
                    currentUser={currentUser}
                    onProjectsChanged={handleGlobalRefresh}
                    onCountChanged={setOpenDeletionCount}
                />
            )}

            {/* Lernzentrum: laedt Fragen und Kapitel selbst (fail-open) und
                braucht deshalb nichts aus dieser Ansicht. */}
            {tab === 'lernzentrum' && <AdminLernzentrumView />}
        </div>
    </div>
  );
};
